import type { FC } from 'react';
import { useCallback, useEffect, useRef } from 'react';

import { sendAnalyticsInfo } from 'features/analytics/helpers';
import { useAppDispatch, useAppSelector } from 'features/app/hooks';
import { calleeConnectionStringSelector } from 'features/call/call-base/store';
import { ParticipantsStatusEventBus } from 'features/call/call-status/services';
import { maskPhoneNumber } from 'features/phone-number/helpers';
import { useSignMailHandlers } from 'features/signmail/hooks';
import { signMailGreetingTextSelector } from 'features/signmail/store';
import styled from 'features/theme/styled';
import theme from 'features/theme/theme';
import { useWhileComponentMounted } from 'features/utils/hook';

export const SignMailGreetingTextOnly: FC = () => {
  const text = useAppSelector(signMailGreetingTextSelector);
  const { handleGreetingEnded } = useSignMailHandlers();
  const whileComponentMounted = useWhileComponentMounted();
  const dispatch = useAppDispatch();

  // Get the callee phone number from Redux store
  const calleePhoneNumber =
    useAppSelector(calleeConnectionStringSelector) || '';

  const maskedPhoneNumber = maskPhoneNumber(calleePhoneNumber);

  const timeoutId = useRef<NodeJS.Timeout>();

  const handleClearTimeout = useCallback(() => {
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
      timeoutId.current = undefined;
    }
  }, []);

  const handleTimeoutExpired = useCallback(() => {
    dispatch(
      sendAnalyticsInfo({
        method: 'SignMailGreetingTextOnly Timeout Expired',
        message:
          'SignMailGreetingTextOnly timeout expired attempting to move to next state',
      })
    );
    handleClearTimeout();
    handleGreetingEnded();
  }, [dispatch, handleClearTimeout, handleGreetingEnded]);

  useEffect(() => {
    const subscription = ParticipantsStatusEventBus.deaf.$disconnectSignMail
      .pipe(whileComponentMounted())
      .subscribe(handleClearTimeout);

    return () => {
      subscription.unsubscribe();
    };
  }, [handleClearTimeout, whileComponentMounted]);

  useEffect(() => {
    timeoutId.current = setTimeout(handleTimeoutExpired, 20000);

    return () => {
      clearTimeout(timeoutId.current);
    };
  }, [dispatch, handleTimeoutExpired]);

  return (
    <GreetingTextContainer>
      <GreetingText>{text}</GreetingText>
      <CalleeNumberText>
        {`${maskedPhoneNumber ? `${maskedPhoneNumber} ` : ''}SignMail`}
      </CalleeNumberText>
    </GreetingTextContainer>
  );
};

const GreetingTextContainer = styled('div')`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  width: 100%;
  border: 2px solid yellow;
`;

const GreetingText = styled('div')`
  font-size: 24px;
  color: ${theme.palette.common.white};
  text-align: center;
`;

const CalleeNumberText = styled('div')`
  position: absolute;
  bottom: 20px;
  left: 20px;
  color: white;
  font-size: 18px;
  z-index: 10;
  background-color: rgba(0, 0, 0, 0.7);
  padding: 8px 12px;
  border-radius: 4px;
`;
